import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { motion, AnimatePresence } from "framer-motion";
import { AlertCircle, ArrowRight, MessageCircle, Mail, Ship, MapPin, Boxes } from "lucide-react";
import SEO from "@/components/common/SEO";
import PageHero from "@/components/common/PageHero";
import { CTAButton } from "@/components/common/CTA";
import HsCodeField from "@/components/common/HsCodeField";
import api from "@/lib/api";
import {
  countryOptions, incotermOptions, unitOptions, cargoNatureOptions, loadingTerms,
  freightModes, containerTypes, contact, waLink,
} from "@/config/site";

const Label = ({ children, required, htmlFor }) => (
  <label htmlFor={htmlFor} className="block text-[13px] font-semibold text-navy mb-1.5">
    {children} {required && <span className="text-[#B42318]">*</span>}
  </label>
);
const fieldCls =
  "w-full min-h-[48px] rounded-md border border-[#D9E0E4] bg-white px-3.5 text-[15px] text-navy " +
  "placeholder:text-[#9AA6AE] focus:outline-none focus:ring-2 focus:ring-teal focus:border-teal transition-colors";
const Err = ({ msg }) => msg ? (
  <p className="mt-1 text-[12.5px] text-[#B42318] flex items-center gap-1" role="alert"><AlertCircle className="h-3.5 w-3.5" /> {msg}</p>
) : null;

export default function RateQuote() {
  const navigate = useNavigate();
  const { register, handleSubmit, setValue, watch, formState: { errors } } = useForm({ mode: "onBlur" });
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [pending, setPending] = useState(null);

  const onSubmit = async (data) => {
    if (data._hp) return;
    setSubmitError(null); setSubmitting(true); setPending(data);
    try {
      const payload = {
        enquiry_type: "freight_rate",
        service: "Logistics Coordination",
        full_name: data.full_name,
        company_name: data.company_name,
        work_email: data.work_email,
        phone: data.phone,
        country: data.country,
        commodity: data.commodity,
        product_name: data.commodity,
        hs_code: data.hs_code,
        unit: data.unit,
        mode: data.mode,
        origin_city: data.origin_city,
        origin_term: data.origin_term,
        sourcing_country: data.origin_city,
        destination_city: data.destination_city,
        destination_term: data.destination_term,
        delivery_destination: data.destination_city,
        cargo_nature: data.cargo_nature,
        container_type: data.container_type,
        cargo_volume: data.cargo_volume,
        cargo_ready_date: data.cargo_ready_date,
        incoterm: data.incoterm,
        message: data.message,
        consent: !!data.consent,
      };
      const res = await api.post("/quotes", payload);
      navigate("/thank-you", { state: { reference: res.data.reference, name: res.data.full_name } });
    } catch (e) {
      setSubmitError(e.response?.data?.detail || "Something went wrong. Please try again.");
      setSubmitting(false);
    }
  };

  return (
    <>
      <SEO
        title="Freight Rate Enquiry | Premier Global Sourcing"
        description="Request an indicative freight rate. Share your commodity, origin and destination, cargo type and container details and our team will coordinate options with partners."
        path="/rate-quote"
      />
      <PageHero
        eyebrow="Freight rate enquiry"
        title="Request a rate quotation"
        intro="Tell us your route and cargo. We will coordinate indicative air, sea or land freight options with relevant qualified partners for your evaluation."
        breadcrumbs={[{ label: "Freight Rate Enquiry" }]}
      />

      <section className="container-page py-16 lg:py-24 grid gap-10 lg:grid-cols-12">
        <div className="lg:col-span-8">
          <form onSubmit={handleSubmit(onSubmit)} noValidate data-testid="rate-form" className="space-y-12">
            <input type="text" tabIndex={-1} autoComplete="off" className="hidden" aria-hidden="true" {...register("_hp")} />

            {/* Cargo & route */}
            <fieldset>
              <legend className="font-heading font-bold text-[20px] text-navy mb-1 flex items-center gap-2"><MapPin className="h-5 w-5 text-teal" /> 1. Route & cargo</legend>
              <p className="text-[14px] text-muted-foreground mb-6">Where is it moving from and to, and what are you shipping?</p>
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <Label htmlFor="commodity" required>Commodity description</Label>
                  <input id="commodity" className={fieldCls} placeholder="e.g. Ceramic floor tiles" data-testid="rf-commodity"
                    {...register("commodity", { required: "Please describe the commodity." })} />
                  <Err msg={errors.commodity?.message} />
                </div>
                <div>
                  <Label htmlFor="hs_code">HS Code <span className="text-muted-foreground font-normal">(type code or description)</span></Label>
                  <HsCodeField
                    value={watch("hs_code") || ""}
                    onChange={(v) => setValue("hs_code", v)}
                    onSelect={(s) => { setValue("hs_code", s.code); if (s.unit) setValue("unit", s.unit); }}
                    testid="rf-hs-code"
                  />
                  <input type="hidden" {...register("hs_code")} />
                </div>
                <div>
                  <Label htmlFor="origin_city" required>Origin (city, country)</Label>
                  <input id="origin_city" className={fieldCls} placeholder="e.g. Shanghai, China" data-testid="rf-origin"
                    {...register("origin_city", { required: "Enter the origin." })} />
                  <Err msg={errors.origin_city?.message} />
                </div>
                <div>
                  <Label htmlFor="origin_term">Origin service</Label>
                  <select id="origin_term" className={fieldCls} defaultValue="" data-testid="rf-origin-term" {...register("origin_term")}>
                    <option value="" disabled>Select CY / Door</option>
                    {loadingTerms.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <Label htmlFor="destination_city" required>Destination (city, country)</Label>
                  <input id="destination_city" className={fieldCls} placeholder="e.g. Dubai, UAE" data-testid="rf-destination"
                    {...register("destination_city", { required: "Enter the destination." })} />
                  <Err msg={errors.destination_city?.message} />
                </div>
                <div>
                  <Label htmlFor="destination_term">Destination service</Label>
                  <select id="destination_term" className={fieldCls} defaultValue="" data-testid="rf-destination-term" {...register("destination_term")}>
                    <option value="" disabled>Select CY / Door</option>
                    {loadingTerms.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
              </div>
            </fieldset>

            {/* Container / cargo details */}
            <fieldset>
              <legend className="font-heading font-bold text-[20px] text-navy mb-1 flex items-center gap-2"><Boxes className="h-5 w-5 text-teal" /> 2. Container & cargo details</legend>
              <p className="text-[14px] text-muted-foreground mb-6">Help us size the right transport option.</p>
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <Label htmlFor="mode">Transport mode</Label>
                  <select id="mode" className={fieldCls} defaultValue="" data-testid="rf-mode" {...register("mode")}>
                    <option value="" disabled>Select mode</option>
                    {freightModes.map((m) => <option key={m} value={m}>{m}</option>)}
                  </select>
                </div>
                <div>
                  <Label htmlFor="container_type">Container type / load</Label>
                  <select id="container_type" className={fieldCls} defaultValue="" data-testid="rf-container" {...register("container_type")}>
                    <option value="" disabled>Select container</option>
                    {containerTypes.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <Label htmlFor="cargo_nature">Cargo nature</Label>
                  <select id="cargo_nature" className={fieldCls} defaultValue="" data-testid="rf-nature" {...register("cargo_nature")}>
                    <option value="" disabled>Select nature</option>
                    {cargoNatureOptions.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <Label htmlFor="cargo_volume">Estimated volume / weight</Label>
                  <input id="cargo_volume" className={fieldCls} placeholder="e.g. 28 CBM / 12,000 kg" data-testid="rf-volume" {...register("cargo_volume")} />
                </div>
                <div>
                  <Label htmlFor="unit">Unit of measure <span className="text-muted-foreground font-normal">(auto-set from HS code)</span></Label>
                  <input id="unit" list="rf-units" className={fieldCls} placeholder="e.g. units (pcs)" data-testid="rf-unit" {...register("unit")} />
                  <datalist id="rf-units">
                    {unitOptions.map((u) => <option key={u} value={u} />)}
                  </datalist>
                </div>
                <div>
                  <Label htmlFor="cargo_ready_date">Cargo ready date</Label>
                  <input id="cargo_ready_date" type="date" className={fieldCls} data-testid="rf-ready-date" {...register("cargo_ready_date")} />
                </div>
                <div>
                  <Label htmlFor="incoterm">Incoterm <span className="text-muted-foreground font-normal">(optional)</span></Label>
                  <select id="incoterm" className={fieldCls} defaultValue="" data-testid="rf-incoterm" {...register("incoterm")}>
                    <option value="" disabled>Select incoterm</option>
                    {incotermOptions.map((i) => <option key={i} value={i}>{i}</option>)}
                  </select>
                </div>
              </div>
            </fieldset>

            {/* Contact */}
            <fieldset>
              <legend className="font-heading font-bold text-[20px] text-navy mb-1 flex items-center gap-2"><Ship className="h-5 w-5 text-teal" /> 3. Your contact details</legend>
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <Label htmlFor="full_name" required>Full name</Label>
                  <input id="full_name" className={fieldCls} data-testid="rf-name" {...register("full_name", { required: "Please enter your name." })} />
                  <Err msg={errors.full_name?.message} />
                </div>
                <div>
                  <Label htmlFor="company_name">Company name</Label>
                  <input id="company_name" className={fieldCls} data-testid="rf-company" {...register("company_name")} />
                </div>
                <div>
                  <Label htmlFor="work_email" required>Work email</Label>
                  <input id="work_email" type="email" className={fieldCls} data-testid="rf-email"
                    {...register("work_email", { required: "Please enter your email.", pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: "Enter a valid email." } })} />
                  <Err msg={errors.work_email?.message} />
                </div>
                <div>
                  <Label htmlFor="phone" required>Phone / WhatsApp</Label>
                  <input id="phone" className={fieldCls} data-testid="rf-phone" {...register("phone", { required: "Please enter a phone number." })} />
                  <Err msg={errors.phone?.message} />
                </div>
                <div>
                  <Label htmlFor="country">Country</Label>
                  <select id="country" className={fieldCls} defaultValue="" data-testid="rf-country" {...register("country")}>
                    <option value="" disabled>Select country</option>
                    {countryOptions.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div className="sm:col-span-2">
                  <Label htmlFor="message">Comments</Label>
                  <textarea id="message" rows={3} className={`${fieldCls} py-3 min-h-[90px]`} data-testid="rf-message" {...register("message")} />
                </div>
              </div>
              <div className="mt-5 flex items-start gap-3">
                <input id="consent" type="checkbox" className="mt-1 h-5 w-5 rounded border-[#D9E0E4] accent-[#0B6E69]" data-testid="rf-consent"
                  {...register("consent", { required: "Please confirm you agree to be contacted." })} />
                <label htmlFor="consent" className="text-[13.5px] text-muted-foreground leading-relaxed">
                  I agree that Premier Global Sourcing may use these details to respond to my rate enquiry, in line with the Privacy Policy. <span className="text-[#B42318]">*</span>
                </label>
              </div>
              <Err msg={errors.consent?.message} />
            </fieldset>

            <AnimatePresence>
              {submitError && (
                <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: "auto" }} exit={{ opacity: 0, height: 0 }}
                  className="rounded-md border border-[#F1C7C3] bg-[#FBEBEA] p-4" data-testid="rf-error" role="alert">
                  <p className="text-[14px] text-[#B42318] font-medium flex items-center gap-2"><AlertCircle className="h-4 w-4" /> {submitError}</p>
                  <div className="mt-3 flex flex-wrap gap-3">
                    <button type="button" onClick={() => pending && onSubmit(pending)} className="text-[13px] font-semibold text-[#B42318] underline" data-testid="rf-retry">Try again</button>
                    <a href={waLink()} target="_blank" rel="noopener noreferrer" className="text-[13px] font-semibold text-teal inline-flex items-center gap-1"><MessageCircle className="h-3.5 w-3.5" /> WhatsApp</a>
                    <a href={`mailto:${contact.email}`} className="text-[13px] font-semibold text-teal inline-flex items-center gap-1"><Mail className="h-3.5 w-3.5" /> Email us</a>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <CTAButton as="button" type="submit" variant="primary" loading={submitting} disabled={submitting}
                icon={submitting ? undefined : ArrowRight} data-testid="rf-submit">
                {submitting ? "Submitting…" : "Request rate quotation"}
              </CTAButton>
              <p className="text-[12.5px] text-muted-foreground">Rates are indicative and coordinated with qualified freight partners.</p>
            </div>
          </form>
        </div>

        <aside className="lg:col-span-4">
          <div className="sticky top-24 space-y-5">
            <div className="rounded-lg bg-navy text-white p-6">
              <h2 className="font-heading font-bold text-[17px]">Not a carrier — a coordinator</h2>
              <p className="mt-2 text-[13.5px] text-white/75 leading-relaxed">
                We do not own vessels, aircraft or trucks. We coordinate air, sea and land freight options
                with relevant qualified partners so you can evaluate routes, transit times and costs.
              </p>
            </div>
            <div className="rounded-lg border border-[#D9E0E4] bg-white p-6">
              <p className="text-[13px] font-semibold uppercase tracking-wide text-teal">Already enquired?</p>
              <p className="mt-2 text-[13.5px] text-muted-foreground">Track the status of an existing enquiry with your reference number.</p>
              <CTAButton to="/track" variant="outline" className="mt-4 w-full" data-testid="rf-track-link">Track your enquiry</CTAButton>
            </div>
          </div>
        </aside>
      </section>
    </>
  );
}
